import { createFrontendModule, PageBlueprint } from '@backstage/frontend-plugin-api';
import {
  compatWrapper,
  convertLegacyPlugin,
  convertLegacyPageExtension,
} from '@backstage/core-compat-api';
import { EntityContentBlueprint } from '@backstage/plugin-catalog-react/alpha';
import { openCostPlugin, OpenCostPage } from '@backstage-community/plugin-opencost';
import { argoWorkflowsPlugin } from '@internal/plugin-argo-workflows';

// Kubernetes overview — custom standalone page (avoids /kubernetes which conflicts
// with the auto-discovered kubernetes plugin page that requires entity context)
const kubernetesOverviewPage = PageBlueprint.make({
  name: 'kubernetes-overview',
  params: {
    path: '/infrastructure',
    loader: async () => {
      const { KubernetesOverviewPage } = await import(
        '../components/KubernetesOverviewPage'
      );
      return compatWrapper(<KubernetesOverviewPage />);
    },
  },
});

const platformDashboardPage = PageBlueprint.make({
  name: 'platform-dashboard',
  params: {
    path: '/',
    noHeader: true,
    loader: async () => {
      const { PlatformDashboardPage } = await import(
        '../components/PlatformDashboardPage'
      );
      return compatWrapper(<PlatformDashboardPage />);
    },
  },
});

const tvDashboardPage = PageBlueprint.make({
  name: 'tv-dashboard',
  params: {
    path: '/tv',
    noHeader: true,
    loader: async () => {
      const { TvDashboardPage } = await import(
        '../components/TvDashboardPage'
      );
      return compatWrapper(<TvDashboardPage />);
    },
  },
});

const visionDashboardPage = PageBlueprint.make({
  name: 'vision-dashboard',
  params: {
    path: '/vision',
    loader: async () => {
      const { VisionDashboardPage } = await import(
        '../components/VisionDashboardPage'
      );
      return compatWrapper(<VisionDashboardPage />);
    },
  },
});

export const pagesModule = createFrontendModule({
  pluginId: 'app',
  extensions: [kubernetesOverviewPage, platformDashboardPage, tvDashboardPage, visionDashboardPage],
});

// Argo Workflows — convert legacy plugin so its API (argoWorkflowsApiRef) is registered,
// then provide a standalone page that reads config instead of entity annotations
const argoStandalonePage = PageBlueprint.make({
  name: 'argo-workflows-overview',
  params: {
    path: '/argo-workflows',
    loader: async () => {
      const { ArgoWorkflowsStandalonePage } = await import(
        '../components/ArgoWorkflowsStandalonePage'
      );
      return compatWrapper(<ArgoWorkflowsStandalonePage />);
    },
  },
});

const argoWorkflowDetailPage = PageBlueprint.make({
  name: 'argo-workflow-detail',
  params: {
    path: '/argo-workflows/:namespace/:name',
    loader: async () => {
      const { ArgoWorkflowDetailPage } = await import(
        '../components/ArgoWorkflowDetailPage'
      );
      return compatWrapper(<ArgoWorkflowDetailPage />);
    },
  },
});

export const argoWorkflowsNewPlugin = convertLegacyPlugin(argoWorkflowsPlugin, {
  extensions: [argoStandalonePage, argoWorkflowDetailPage],
});

// OpenCost — convert legacy plugin; EUR default set via sidebar link query param
export const openCostNewPlugin = convertLegacyPlugin(openCostPlugin, {
  extensions: [convertLegacyPageExtension(OpenCostPage, { path: '/opencost' })],
});

// Entity Logs tab — shows pod logs on entities with k8s annotations
const entityLogsContent = EntityContentBlueprint.make({
  name: 'entity-logs',
  params: {
    path: '/logs',
    title: 'Logs',
    filter: (entity) =>
      !!(
        entity.metadata.annotations?.['backstage.io/kubernetes-label-selector'] ||
        entity.metadata.annotations?.['backstage.io/kubernetes-id']
      ),
    loader: async () => {
      const { EntityLogsContent } = await import(
        '../components/EntityLogsContent'
      );
      return compatWrapper(<EntityLogsContent />);
    },
  },
});

export const entityLogsModule = createFrontendModule({
  pluginId: 'catalog',
  extensions: [entityLogsContent],
});
